import {
    IconButton,
    Menu,
    Typography,
    Popover,
    useTheme,
    styled,
    Divider,
} from "@mui/material";
import { ExpandMore, FilterAlt, Cancel } from "@mui/icons-material";
import { Grid } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React, { useMemo, useRef, useState } from "react";
import { ApplicationFilterDomain } from "services/ApplicationFiltersService";
import {
    ApplicationFiltersStore,
    SavedFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import { useStore } from "utils/useStore";
import ManageApplicationFilters from "./ManageApplicationFilters";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";

interface ManageFiltersButtonProps {
    onCancel?(): void;
    onApply?(instanceStore: ApplicationFiltersInstanceStore): void;
    title?: string;
    subtitle?: string;
    savedFilterDomains?:
        | readonly ApplicationFilterDomain[]
        | ApplicationFilterDomain[];
    disabled?: boolean;
    domainName?: string;
}

const ManageFiltersButton = observer(function (
    props: ManageFiltersButtonProps,
) {
    const theme = useTheme();

    const applicationFiltersStore = useStore(ApplicationFiltersStore);
    const anchorEl = useRef<HTMLElement | null>(null);
    const [isSavedFiltersOpen, setIsSavedFiltersOpen] = useState(false);
    const [isManageFiltersOpen, setIsManageFiltersOpen] = useState(false);
    const signalsPageStore = useStore(SignalsPageStore);

    const filterLabel = useMemo(() => {
        const appliedFilterCount =
            applicationFiltersStore.appliedFilterCount ?? 0;
        const selectedFilterName =
            applicationFiltersStore.selectedSavedFilter?.name;

        if (!!selectedFilterName) return selectedFilterName;
        else if (appliedFilterCount === 1) return "1 Filter Applied";
        else if (appliedFilterCount > 1)
            return `${appliedFilterCount} Filters Applied`;
        return "Filter";
    }, [
        applicationFiltersStore.selectedSavedFilter,
        applicationFiltersStore.appliedFilterCount,
    ]);

    return (
        <>
            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{
                    padding: 0,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.grey[300]}`,
                    width: "fit-content",
                    backgroundColor: theme.palette.white.main,
                }}
                width="fit-content"
                flexWrap="nowrap"
            >
                <Grid item ref={(elRef) => (anchorEl.current = elRef)}>
                    <AcxButton
                        id="signals-filters-filter-builder"
                        disableRipple={props.disabled}
                        onClick={() => {
                            if (props.disabled) return;
                            setIsSavedFiltersOpen(false);
                            setIsManageFiltersOpen((open) => !open);
                            // in the case of saving a filter and closing/reopening we need to re-apply saved filter
                            if (
                                !!applicationFiltersStore.selectedSavedFilter
                                    ?.id
                            ) {
                                signalsPageStore.setShouldResetBuilder(false);
                                applicationFiltersStore.applySavedFilters(
                                    applicationFiltersStore.selectedSavedFilter
                                        ?.id,
                                );
                            }
                        }}
                        variant="outlined"
                        color="primary"
                        sx={{
                            color: "black",
                            border: "none",
                            borderRight: !props.disabled
                                ? `1px solid ${theme.palette.grey[300]}`
                                : "none",
                            borderRadius: 0,
                            fontWeight: "normal",

                            "&:hover": {
                                cursor: !props.disabled ? "pointer" : "default",
                                backgroundColor: "transparent",
                                border: "none",
                                borderRight: !props.disabled
                                    ? `1px solid ${theme.palette.grey[300]}`
                                    : "none",
                                color: "black",
                            },
                        }}
                    >
                        <FilterAlt
                            fontSize="small"
                            sx={(theme) => ({
                                marginRight: theme.spacing(1),
                            })}
                            color="action"
                        />
                        <Typography
                            sx={{
                                maxWidth: "164px",
                                overflowX: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {filterLabel}
                        </Typography>
                        {!!applicationFiltersStore.selectedSavedFilter &&
                            !props.disabled &&
                            !signalsPageStore.currentDashboard
                                ?.pinnedApplicationFilterId && (
                                <IconButton
                                    size="small"
                                    sx={(theme) => ({
                                        marginLeft: theme.spacing(1),
                                        fontSize: "16px",
                                    })}
                                    onClick={(event) => {
                                        if (!signalsPageStore.currentDashboard) return;
                                        event.stopPropagation();
                                        applicationFiltersStore.resetFilters();
                                        applicationFiltersStore.setSelectedSavedFilters(
                                            undefined,
                                        );
                                        applicationFiltersStore.quickFiltersStore.resetFilters();

                                        signalsPageStore.currentDashboard.applyFilters(
                                        );
                                    }}
                                >
                                    <Cancel
                                        fontSize="inherit"
                                        htmlColor={theme.palette.gray[300]}
                                    />
                                </IconButton>
                            )}
                    </AcxButton>
                </Grid>
                {!props.disabled && (
                    <Grid item>
                        <IconButton
                            id="signals-filters-saved-filters-dropdown"
                            size="small"
                            onClick={() => {
                                setIsManageFiltersOpen(false);
                                setIsSavedFiltersOpen((open) => !open);
                            }}
                            sx={{
                                padding: 0,
                            }}
                        >
                            <ExpandMore />
                        </IconButton>
                    </Grid>
                )}
            </Grid>
            <Popover
                open={isManageFiltersOpen}
                onClose={() =>
                    setIsManageFiltersOpen(!isManageFiltersOpen)
                }
                anchorEl={anchorEl.current}
                anchorReference="anchorEl"
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: -8,
                    horizontal: 0,
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: "950px",
                            height: "55dvh",
                            borderRadius: "8px",
                        },
                    },
                }}
            >
                <ManageApplicationFilters
                    onApply={(instanceStore) => {
                        const quickFilters =
                            applicationFiltersStore.quickFiltersStore;
                        if (!applicationFiltersStore.selectedSavedFilter) {
                            instanceStore.setStartDate(quickFilters.startDate);
                            instanceStore.setEndDate(quickFilters.endDate);
                            instanceStore.setDateReferenceOption(
                                quickFilters.dateReferenceOption,
                            );
                            instanceStore.setDateRangeLabel(
                                quickFilters.dateRangeLabel,
                            );
                            instanceStore.setHierarchyIds(
                                quickFilters.hierarchyIds,
                            );
                            instanceStore.setHierarchyLabels(
                                quickFilters.hierarchyLabels,
                            );
                            instanceStore.setSelectedCategories(
                                quickFilters.selectedCategories ?? [],
                                quickFilters.selectedSubcategories ?? [],
                            );
                        }

                        if (
                            !instanceStore.startDate &&
                            !instanceStore.endDate
                        ) {
                            instanceStore.setStartDate(quickFilters.startDate);
                            instanceStore.setEndDate(quickFilters.endDate);
                        }

                        // Apply to quick filters first so they reflect builder selections
                        applicationFiltersStore.quickFiltersStore.applyExternalFilters(
                            instanceStore,
                        );
                        // Sync quick filters into main to ensure they are included in requests
                        applicationFiltersStore.syncQuickFiltersIntoMain();

                        // Then apply full set (including non-quick fields) to main store
                        applicationFiltersStore.applyExternalFilters(
                            instanceStore,
                        );

                        props.onApply?.(instanceStore);
                        setIsManageFiltersOpen(false);
                    }}
                    onCancel={() => {
                        props.onCancel?.();
                        setIsManageFiltersOpen(false);
                    }}
                    title={props.title}
                    subtitle={props.subtitle}
                    domainName={props?.domainName}
                    setIsManageFiltersOpen={setIsManageFiltersOpen}
                />
            </Popover>
            <Menu
                open={isSavedFiltersOpen}
                onClose={() => setIsSavedFiltersOpen(false)}
                anchorEl={anchorEl.current}
                anchorReference="anchorEl"
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: -8,
                    horizontal: 0,
                }}
                slotProps={{
                    paper: {
                        sx: (theme) => ({
                            padding: theme.spacing(1),
                            borderRadius: 2,
                        }),
                    },
                }}
            >
                <SavedFiltersList
                    onSelect={async (filter) => {
                        signalsPageStore.setShouldResetBuilder(false);
                        const promise = applicationFiltersStore.applySavedFilters(
                            filter.id,
                        );
                        setIsSavedFiltersOpen(false);
                        await promise;
                        if (!signalsPageStore.currentDashboard) return;
                        signalsPageStore.currentDashboard.applyFilters();
                    }}
                    ignoreChildrenWhenNoSavedFilters={false}
                >
                    <SavedFiltersList.Action
                        id="signals-filters-add-saved-filter"
                        onClick={() => {
                            if (!signalsPageStore.currentDashboard) return;
                            setIsSavedFiltersOpen(false);
                            applicationFiltersStore.resetFilters();
                            applicationFiltersStore.setSelectedSavedFilters(
                                undefined,
                            );
                            applicationFiltersStore.quickFiltersStore.resetFilters();
                            signalsPageStore.currentDashboard.applyFilters();
                            signalsPageStore.setShouldResetBuilder(true);
                            setIsManageFiltersOpen(true);
                        }}
                    >
                        + Add Saved Filter
                    </SavedFiltersList.Action>
                </SavedFiltersList>
            </Menu>
        </>
    );
});

export default ManageFiltersButton;

interface SavedFiltersListProps {
    domains?: ApplicationFilterDomain[];
    onSelect?(filter: SavedFilter): void;
    ignoreChildrenWhenNoSavedFilters?: boolean;
}

const SavedFilterOption = styled(AcxButton)(({ theme }) => ({
    minWidth: "100%",
    margin: "none",
    padding: theme.spacing(1),
    color: theme.palette.gray[600],
    backgroundColor: theme.palette.white.main,
    justifyContent: "start",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    display: "flex",
    ":hover": {
        backgroundColor: theme.palette.primary[100],
        color: theme.palette.primary[500],
    },
}));

const OptionText = styled("span")(() => ({
    maxWidth: "40ch",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
}));

const GroupTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[500],
    fontWeight: "bold",
    fontSize: "12px",
}));

export function SavedFiltersList({
    children,
    domains,
    ignoreChildrenWhenNoSavedFilters = true,
    onSelect,
    ...props
}: React.PropsWithChildren<
    SavedFiltersListProps &
        Omit<React.ComponentPropsWithoutRef<typeof Grid>, "onSelect">
>) {
    const applicationFiltersStore = useStore(ApplicationFiltersStore);

    const isLoading = applicationFiltersStore.getTaskLoading(
        ApplicationFiltersStore.Tasks.LOAD_SAVED_FILTERS,
    );

    if (isLoading)
        return <AcxLoadingIndicator size={32} alternate="PuffLoader" />;

    const savedFilters: SavedFilter[] =
        applicationFiltersStore.getSavedFiltersByDomains(
            domains ?? ApplicationFiltersStore.AllSavedFilterDomains,
        );

    const showChildren =
        !ignoreChildrenWhenNoSavedFilters ||
        !(ignoreChildrenWhenNoSavedFilters && savedFilters.length === 0);

    const filterSelectItem = (filter: SavedFilter) => (
        <Grid item key={filter.id}>
            <SavedFilterOption onClick={() => onSelect?.(filter)}>
                <OptionText>{filter.name} </OptionText>
            </SavedFilterOption>
        </Grid>
    );

    return (
        <Grid
            direction="column"
            rowGap={1}
            {...props}
            container
            item
            sx={{ fontSize: "14px", maxWidth: "min-content", minWidth: "40ch" }}
        >
            {savedFilters.length === 0 && (
                <Grid item>
                    <Typography sx={(theme) => ({ padding: theme.spacing(1) })}>
                        No Saved Filters
                    </Typography>
                </Grid>
            )}
            {savedFilters.length > 0 && (
                <React.Fragment>
                    <Grid container item>
                        <Grid item>
                            <GroupTitle>My Filters</GroupTitle>
                        </Grid>
                        <Grid container item direction="column" rowGap={1 / 2}>
                            {applicationFiltersStore.mySavedFilters.map(
                                filterSelectItem,
                            )}
                        </Grid>
                    </Grid>
                    <Grid container item>
                        <Grid item>
                            <GroupTitle>Shared Filters</GroupTitle>
                        </Grid>
                        <Grid container item direction="column" rowGap={1 / 2}>
                            {applicationFiltersStore.sharedSavedFilters.map(
                                filterSelectItem,
                            )}
                        </Grid>
                    </Grid>
                </React.Fragment>
            )}
            {showChildren && React.Children.count(children) > 0 && (
                <Grid item>
                    <Divider />
                </Grid>
            )}
            {showChildren && children}
        </Grid>
    );
}
SavedFiltersList.Action = SavedFiltersListAction;

const SavedFiltersListActionButton = styled(AcxButton)(({ theme }) => ({
    justifyContent: "start",
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.white.main,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    padding: theme.spacing(1),
    ":hover": {
        backgroundColor: theme.palette.gray[100],
    },
}));

function SavedFiltersListAction(
    props: React.ComponentPropsWithoutRef<typeof SavedFiltersListActionButton>,
) {
    return (
        <Grid item>
            <SavedFiltersListActionButton {...props} />
        </Grid>
    );
}
